import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Descriptions,
  Tag,
  message,
  Modal,
  Image,
  Spin,
  Empty,
  Row,
  Col,
  Divider,
  List,
  Upload,
  Form,
  Input,
  DatePicker,
  Space,
  Alert,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  UserOutlined,
  DollarOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { courseAPI, registrationAPI } from '../../services/api';
import { paymentAPI, paymentScreenshotAPI } from '../../services/api';
import { Course } from '../../types';
import { useAuth } from '../../context/AuthContext';
import PaymentOCRUpload from '../../components/PaymentOCRUpload';
import PaymentUploadWithAutoRecognition from '../../components/PaymentUploadWithAutoRecognition';

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [paymentQrUrl, setPaymentQrUrl] = useState('');
  const [currentRegistrationId, setCurrentRegistrationId] = useState<number | null>(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [userRegistration, setUserRegistration] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string>('');

  useEffect(() => {
    const fetchCourseData = async () => {
      if (id) {
        try {
          const data = await courseAPI.getCourse(parseInt(id));
          setCourse(data);
          
          // 检查用户是否已报名
          if (isAuthenticated) {
            try {
              const registrations = await registrationAPI.getMyRegistrations();
              const existingRegistration = registrations.find(reg => reg.courseId === parseInt(id!));
              setUserRegistration(existingRegistration || null);
            } catch (error) {
              // 用户未登录或无报名记录
            }
          }
        } catch (error) {
          message.error('获取课程详情失败');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchCourseData();
  }, [id, isAuthenticated]);

  const fetchCourse = async () => {
    try {
      const data = await courseAPI.getCourse(parseInt(id!));
      setCourse(data);
    } catch (error) {
      message.error('获取课程详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!isAuthenticated) {
      message.info('请先登录');
      navigate('/login');
      return;
    }

    setRegistering(true);
    try {
      // 仅验证报名资格，不创建报名记录
      const response = await registrationAPI.registerCourse(parseInt(id!));
      setPaymentQrUrl(response.paymentQrUrl || '');
      setShowPaymentModal(true);
      // 不显示报名成功消息，直接显示支付二维码
    } catch (error: any) {
      message.error(error.response?.data?.message || '报名失败');
    } finally {
      setRegistering(false);
    }
  };

  // 简化支付流程 - 只显示二维码，不进行轮询
  const startPaymentPolling = (registrationId: number) => {
    // 不执行轮询，仅显示二维码
    console.log('Registration created:', registrationId);
  };

  // 手动确认支付（测试用）
  const handleConfirmPayment = async () => {
    if (!currentRegistrationId) return;
    
    setIsCheckingPayment(true);
    try {
      // 在开发环境中，模拟支付成功
      message.success('支付确认成功！');
      setShowPaymentModal(false);
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
        setPaymentCheckInterval(null);
      }
      fetchCourse();
    } catch (error: any) {
      message.error(error.response?.data?.message || '确认支付失败');
    } finally {
      setIsCheckingPayment(false);
    }
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
    };
  }, [paymentCheckInterval]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case '正常': return 'green';
      case '转出': return 'orange';
      case '过期': return 'red';
      case '退课': return 'volcano';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case '正常': return '报名中';
      case '转出': return '已满员';
      case '过期': return '已结束';
      case '退课': return '已取消';
      default: return status;
    }
  };

  const canRegister = () => {
    if (!course) return false;
    
    const startDate = course.startDate;
    return course.status === '正常' && 
           course.currentStudents < course.maxStudents &&
           dayjs(startDate).isAfter(dayjs()) &&
           !userRegistration; // 已经报名的不能再报名
  };

// 移除getRegistrationStatus函数，直接在按钮渲染中判断

  const formatSchedule = (course: Course) => {
    if (!course.startDate || !course.endDate) {
      return '时间待定';
    }
    
    const startDate = dayjs(course.startDate).format('YYYY年MM月DD日');
    const endDate = dayjs(course.endDate).format('YYYY年MM月DD日');
    
    let scheduleInfo = `${startDate} 至 ${endDate}`;
    
    // Add class time description if available
    if (course.classTimeDescription) {
      scheduleInfo += `，${course.classTimeDescription}`;
    }
    
    return scheduleInfo;
  };

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Empty description="课程不存在" />
        <Button onClick={() => navigate('/courses')}>
          返回课程列表
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/courses')}
        style={{ marginBottom: '20px' }}
      >
        返回课程列表
      </Button>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card>
            <h1 style={{ fontSize: '28px', marginBottom: '20px' }}>
              {course.name}
            </h1>
            
            {course.description && (
              <div style={{ marginBottom: '20px' }}>
                <h3>课程介绍</h3>
                <p style={{ color: '#666', lineHeight: '1.8' }}>
                  {course.description}
                </p>
              </div>
            )}

            <Divider />

            <Descriptions bordered column={1}>
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined style={{ marginRight: '8px' }} />
                    上课时间
                  </span>
                }
              >
                {formatSchedule(course)}
              </Descriptions.Item>
              
              
              <Descriptions.Item
                label={
                  <span>
                    <EnvironmentOutlined style={{ marginRight: '8px' }} />
                    上课地点
                  </span>
                }
              >
                {course.location}
              </Descriptions.Item>
              
              <Descriptions.Item
                label="学期"
              >
                <Tag color="green">{course.semester}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item
                label="年级"
              >
                <Tag color="blue">{course.gradeLevel}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item
                label="难度等级"
              >
                <Tag color="orange">{course.difficultyLevel}</Tag>
              </Descriptions.Item>
              
              {course.teacherName && (
                <Descriptions.Item
                  label={
                    <span>
                      <UserOutlined style={{ marginRight: '8px' }} />
                      授课老师
                    </span>
                  }
                >
                  {course.teacherName}
                </Descriptions.Item>
              )}
              
              {course.category && (
                <Descriptions.Item
                  label="课程分类"
                >
                  <Tag color="purple">{course.category}</Tag>
                </Descriptions.Item>
              )}
              
              <Descriptions.Item
                label={
                  <span>
                    <DollarOutlined style={{ marginRight: '8px' }} />
                    课程价格
                  </span>
                }
              >
                <span style={{ color: '#ff4d4f', fontSize: '18px', fontWeight: 'bold' }}>
                  ¥{course.price}
                </span>
              </Descriptions.Item>
              
              <Descriptions.Item
                label={
                  <span>
                    <TeamOutlined style={{ marginRight: '8px' }} />
                    报名人数
                  </span>
                }
              >
                <span style={{
                  color: course.currentStudents >= course.maxStudents ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold'
                }}>
                  {course.currentStudents} / {course.maxStudents}
                </span>
              </Descriptions.Item>
              
              <Descriptions.Item
                label="课程状态"
              >
                <Tag color={getStatusColor(course.status)}>
                  {getStatusText(course.status)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              {course.imageUrl ? (
                <Image
                  src={course.imageUrl}
                  alt={course.name}
                  style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '200px',
                  backgroundColor: '#f0f2f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#999'
                }}>
                  暂无图片
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '10px' }}>
                ¥{course.price}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#666' }}>剩余名额：</span>
                  <span style={{ fontWeight: 'bold', color: course.currentStudents >= course.maxStudents ? '#ff4d4f' : '#52c41a' }}>
                    {course.maxStudents - course.currentStudents}
                  </span>
                </div>
                
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#666' }}>上课时间：</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {formatSchedule(course)}
                  </span>
                </div>
              </div>

              {(() => {
                const isPaid = userRegistration && (userRegistration.status === 'confirmed' || userRegistration.status === '已付款');
                
                if (isPaid) {
                  return (
                    <Button type="primary" size="large" block disabled>
                      已报名
                    </Button>
                  );
                }
                
                return (
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handleRegister}
                    loading={registering}
                    disabled={!canRegister()}
                  >
                    {!canRegister() ? '不可报名' : '立即报名'}
                  </Button>
                );
              })()}

              {!canRegister() && !userRegistration && (
                <div style={{ marginTop: '10px', color: '#ff4d4f', fontSize: '12px' }}>
                  {course.status === '转出' && '课程已满员'}
                  {course.status === '过期' && '课程已结束'}
                  {course.status === '退课' && '课程已取消'}
                  {dayjs(course.startDate).isBefore(dayjs()) && '课程已开始'}
                </div>
              )}
            </div>
          </Card>

        </Col>
      </Row>

      <Modal
        title="支付说明"
        visible={showPaymentModal}
        onCancel={() => {
          setShowPaymentModal(false);
        }}
        footer={[
          <Button key="close" onClick={() => setShowPaymentModal(false)}>
            关闭
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center' }}>
          <h3>请使用支付宝扫码支付</h3>
          <p>金额：¥{course.price}</p>
          
          {/* 收款码 */}
          <div style={{ textAlign: 'center', margin: '20px auto' }}>
            <Image
              src="/qrcode.jpg"
              alt="收款二维码"
              style={{ 
                width: '200px', 
                height: '200px',
                borderRadius: '8px',
                border: '1px solid #eee'
              }}
              preview={false}
            />
          </div>
          
          <Alert
            message="支付说明"
            description={
              <div style={{ textAlign: 'left' }}>
                <p>1. 请使用支付宝扫描上方二维码完成支付</p>
                <p>2. 支付完成后，点击下方上传支付截图</p>
                <p>3. 系统会自动识别订单号，识别成功后报名成功</p>
                <p><strong>注意：截图必须包含订单号才能识别成功</strong></p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16, marginBottom: 16 }}
          />
          
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>上传支付截图</p>
            
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>示例：请上传类似的支付截图</p>
              <Image
                src="/uploads/WechatIMG235.jpg"
                alt="支付截图示例"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px', 
                  borderRadius: '8px',
                  border: '1px solid #eee',
                  marginBottom: 8
                }}
                preview={false}
              />
            </div>
            
            <PaymentUploadWithAutoRecognition
              courseId={id!}
              onUploadComplete={(result) => {
                if (result.success) {
                  message.success('支付截图上传成功！');
                }
              }}
              onRecognitionComplete={(result) => {
                if (result.success) {
                  // 检查是否真的报名成功 (autoApproved)
                  if (result.ocrResult?.autoApproved) {
                    message.success('🎉 恭喜！支付验证成功，报名已完成！');
                    // 延迟5秒后关闭模态框，让用户充分看到成功消息
                    setTimeout(() => {
                      setShowPaymentModal(false);
                      
                      // 更新用户报名状态
                      setUserRegistration({
                        id: result.registrationId,
                        status: 'confirmed',
                        paymentStatus: 'paid',
                        registrationTime: new Date().toISOString()
                      });
                      
                      // 刷新课程信息
                      fetchCourse();
                    }, 5000);
                  } else {
                    // 上传成功但需要人工审核
                    message.info('✅ 截图已上传，正在等待管理员审核...');
                    // 延迟5秒后关闭模态框
                    setTimeout(() => {
                      setShowPaymentModal(false);
                    }, 5000);
                  }
                } else {
                  // 金额不匹配等验证失败 - 不再关闭模态框，让组件内部处理
                  console.log('🚨 验证失败，保持模态框开启:', result);
                  // 不关闭模态框，让用户在模态框内看到错误信息
                }
              }}
              showProgress={true}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseDetail;
